import { motion } from 'framer-motion';
import { ArrowUpRight, Leaf } from 'lucide-react';
import { ESSAYS, RITUAL } from './data';

// HOME — botanical magazine spread. Soft linen + sage. Hero is a SINGLE
// magazine-spread image to the left, with a delicate type-set headline right.
// NO marquee. Distinctive: a "petal" decoration in the hero corner.
export default function Home({ setPage }) {
  return (
    <div className="bg-[#F2EBE0] text-[#1B1A18]">
      <section className="max-w-[1500px] mx-auto px-8 lg:px-14 pt-12 pb-20 grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-20 items-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4 }} className="relative aspect-[4/5] overflow-hidden">
          <img src="https://images.pexels.com/photos/3762879/pexels-photo-3762879.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover" />
          <div className="absolute top-6 left-6 bg-[#F2EBE0]/85 backdrop-blur-sm py-2 px-4 font-mono text-[9px] tracking-[0.4em] uppercase text-[#1B1A18]/85">— Maison · est. 1972</div>
          <Leaf className="absolute bottom-6 right-6 w-12 h-12 text-[#F2EBE0]/85" strokeWidth={0.7} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}>
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B8654A] mb-6">— A skincare house in Aix</p>
          <h1 className="font-serif text-[48px] sm:text-[72px] lg:text-[120px] leading-[0.92] tracking-[-0.02em]">A little, <span className="italic text-[#6F7E62]">slowly</span>.</h1>
          <p className="font-serif italic text-xl lg:text-2xl text-[#1B1A18]/75 mt-8 leading-[1.4] max-w-md">Three formulations. One field. The same farmer for forty years.</p>
          <p className="text-base text-[#1B1A18]/65 mt-8 leading-[1.7] max-w-md">We make twelve thousand jars a year and not one more. Each is poured by hand, into glass, in our atelier in the field.</p>
          <div className="mt-10 flex items-center gap-6">
            <button onClick={() => setPage('ritual')} className="bg-[#1B1A18] text-[#F2EBE0] px-9 py-4 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#B8654A] transition-colors" data-testid="skin-home-ritual-cta">Le rituel</button>
            <button onClick={() => setPage('atelier')} className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#6F7E62] underline-offset-8 hover:text-[#B8654A] hover:underline">L'atelier →</button>
          </div>
        </motion.div>
      </section>

      {/* Three step ritual — magazine columns, sage thread between */}
      <section className="bg-[#E8E0D2] border-y border-[#6F7E62]/15">
        <div className="max-w-[1500px] mx-auto px-8 lg:px-14 py-20 lg:py-28">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B8654A] mb-3">— Le rituel · trois étapes</p>
          <h2 className="font-serif text-4xl lg:text-6xl leading-[0.98] mb-12">A morning, an evening, <span className="italic text-[#6F7E62]">and a Sunday</span>.</h2>
          <div className="grid md:grid-cols-3 gap-px bg-[#6F7E62]/15">
            {RITUAL.map((r, i) => (
              <motion.button key={r.t} onClick={() => setPage('ritual')} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="group bg-[#E8E0D2] p-8 lg:p-10 text-left">
                <div className="aspect-[4/5] overflow-hidden bg-[#F2EBE0] mb-6"><img src={r.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" /></div>
                <p className="font-serif italic text-2xl text-[#B8654A]">{r.step}</p>
                <h3 className="font-serif text-3xl lg:text-4xl mt-1">{r.t}</h3>
                <p className="font-serif italic text-base text-[#1B1A18]/65 mt-4 leading-[1.6]">{r.product}</p>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6F7E62] mt-2">{r.price}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial — three column letterpress essays */}
      <section className="max-w-[1300px] mx-auto px-8 lg:px-14 py-24">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B8654A] mb-3">— Du journal de l'atelier</p>
        <h2 className="font-serif text-3xl lg:text-5xl mb-12">A few <span className="italic text-[#6F7E62]">letters</span>, when there is something to say.</h2>
        <div className="grid md:grid-cols-3 gap-12">
          {ESSAYS.map(e => (
            <article key={e.t} className="group cursor-pointer">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6F7E62]">{e.d}</p>
              <h3 className="font-serif text-2xl lg:text-3xl mt-3 leading-tight group-hover:italic">{e.t}</h3>
              <p className="font-serif italic text-base text-[#1B1A18]/60 mt-2">{e.sub}</p>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#B8654A] mt-5 inline-flex items-center gap-1">{e.read} <ArrowUpRight className="w-3 h-3" /></p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
